import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'designSystem/components/Dialog'
import { Input } from 'designSystem/components/Input'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { TagListCoverage } from 'features/timeSeries/components/panes/TagBrowserList'
import {
  KeywordChip,
  TagFilterRow,
} from 'features/timeSeries/components/panes/TagFilterControls'
import {
  ALL_SOURCES,
  DataFilter,
  emptyTagListCopy,
  MIN_SEARCH_LENGTH,
  toggleTagSelection,
  useAvailabilityFilteredTags,
  useTagSourceOptions,
} from 'features/timeSeries/components/panes/tagFilters'
import {
  ChipsRow,
  FiltersRow,
  ListRegion,
  SearchRegion,
  SearchRow,
  StatusLine,
  TableHeaderRow,
  UnitHeaderCell,
} from 'features/timeSeries/components/panes/tagSearchDialog.styles'
import {
  DialogRowData,
  TagSearchDialogRow,
} from 'features/timeSeries/components/panes/TagSearchDialogRow'
import { useElementSize } from 'features/timeSeries/components/panes/useElementSize'
import { MAX_PENS } from 'features/timeSeries/config/constants'
import { useKeywordManagement } from 'features/timeSeries/hooks/useKeywordManagement'
import { useTagFiltering } from 'features/timeSeries/hooks/useTagFiltering'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FixedSizeList } from 'react-window'
import { BaseSensorEntry } from 'types'

interface TagSearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  coverage?: TagListCoverage
}

export const TagSearchDialog: React.FC<TagSearchDialogProps> = ({
  open,
  coverage,
  onOpenChange,
}) => {
  const { t } = useLingui()

  const { sensorEntries, selectedSensors, setSelectedSensors } =
    useScatterplotContext()
  const [draft, setDraft] = useState<BaseSensorEntry[]>([])
  const [source, setSource] = useState(ALL_SOURCES)
  const [dataFilter, setDataFilter] = useState<DataFilter>('all')
  const [listRef, listSize] = useElementSize()

  // Each open starts from the live selection; edits stay in the draft until
  // Save, so dismissing the dialog applies nothing.
  useEffect(() => {
    if (open) setDraft(selectedSensors)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const {
    tagNameSearch,
    setTagNameSearch,
    tagDescriptionSearch,
    setTagDescriptionSearch,
    tagNameKeywords,
    setTagNameKeywords,
    tagDescriptionKeywords,
    setTagDescriptionKeywords,
    filterMode,
    filteredTags,
    handleToggleFilterMode,
  } = useTagFiltering({ tags: sensorEntries })
  const {
    handleRemoveTagNameKeyword,
    handleRemoveTagDescriptionKeyword,
    handleTagNameKeyDown,
    handleTagDescriptionKeyDown,
  } = useKeywordManagement({
    tagNameSearch,
    tagDescriptionSearch,
    tagNameKeywords,
    tagDescriptionKeywords,
    setTagNameKeywords,
    setTagDescriptionKeywords,
    setTagNameSearch,
    setTagDescriptionSearch,
  })

  const sources = useTagSourceOptions(sensorEntries)
  const hasMeaningfulSearch =
    tagNameSearch.trim().length >= MIN_SEARCH_LENGTH ||
    tagDescriptionSearch.trim().length >= MIN_SEARCH_LENGTH ||
    tagNameKeywords.length > 0 ||
    tagDescriptionKeywords.length > 0
  const { displayed, isCheckingAvailability } = useAvailabilityFilteredTags(
    filteredTags,
    source,
    dataFilter,
    hasMeaningfulSearch
  )

  const draftIds = useMemo(() => new Set(draft.map((tag) => tag.id)), [draft])
  const atPenCap = draft.length >= MAX_PENS

  const toggleDraft = useCallback((tag: BaseSensorEntry) => {
    setDraft((prev) => toggleTagSelection(prev, tag, MAX_PENS))
  }, [])

  const handleSave = () => {
    setSelectedSensors(draft)
    onOpenChange(false)
  }

  const rowData = useMemo<DialogRowData>(
    () => ({ tags: displayed, draftIds, atPenCap, onToggle: toggleDraft }),
    [displayed, draftIds, atPenCap, toggleDraft]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size='lg'>
        <DialogHeader>
          <DialogTitle>
            <Trans>Search tags</Trans>
          </DialogTitle>
        </DialogHeader>
        <SearchRegion>
          <SearchRow>
            <Input
              inputSize='sm'
              placeholder={t`Search by tag name...`}
              value={tagNameSearch}
              onChange={(event) => setTagNameSearch(event.target.value)}
              onKeyDown={handleTagNameKeyDown}
            />
            <ChartTooltip
              label={t`Match tags on name AND description, or either (OR)`}
              side='top'>
              <Button
                variant='outline'
                size='sm'
                onClick={handleToggleFilterMode}>
                {filterMode}
              </Button>
            </ChartTooltip>
            <Input
              inputSize='sm'
              placeholder={t`Search by description...`}
              value={tagDescriptionSearch}
              onChange={(event) => setTagDescriptionSearch(event.target.value)}
              onKeyDown={handleTagDescriptionKeyDown}
            />
          </SearchRow>
          {(tagNameKeywords.length > 0 ||
            tagDescriptionKeywords.length > 0) && (
            <ChipsRow>
              {tagNameKeywords.map((chip) => (
                <KeywordChip
                  key={chip}
                  label={chip}
                  onRemove={() => handleRemoveTagNameKeyword(chip)}
                />
              ))}
              {tagDescriptionKeywords.map((chip) => (
                <KeywordChip
                  key={chip}
                  label={chip}
                  onRemove={() => handleRemoveTagDescriptionKeyword(chip)}
                />
              ))}
            </ChipsRow>
          )}
          <FiltersRow>
            <TagFilterRow
              sources={sources}
              source={source}
              onSourceChange={setSource}
              dataFilter={dataFilter}
              onDataFilterChange={setDataFilter}
              isCheckingAvailability={isCheckingAvailability}
              coverageFilter={coverage?.filter}
              onCoverageFilterChange={coverage?.onFilterChange}
            />
          </FiltersRow>
          <ListRegion ref={listRef}>
            {displayed.length === 0 ? (
              <StatusLine>
                {emptyTagListCopy(dataFilter, hasMeaningfulSearch)}
              </StatusLine>
            ) : (
              <>
                <TableHeaderRow>
                  <span />
                  <span>
                    <Trans>Tag</Trans>
                  </span>
                  <span>
                    <Trans>Description</Trans>
                  </span>
                  <UnitHeaderCell>
                    <Trans>Unit</Trans>
                  </UnitHeaderCell>
                </TableHeaderRow>
                <FixedSizeList
                  height={Math.max(listSize.height - 30, 0)}
                  width='100%'
                  itemCount={displayed.length}
                  itemSize={30}
                  itemData={rowData}>
                  {TagSearchDialogRow}
                </FixedSizeList>
              </>
            )}
          </ListRegion>
        </SearchRegion>
        <DialogFooter>
          <Button variant='ghost' onClick={() => onOpenChange(false)}>
            <Trans>Close</Trans>
          </Button>
          <Button onClick={handleSave}>
            <Trans>Save</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
